import {
	Node,
	NodeRange,
	NodeType,
	ResolvedPos,
	Schema
} from "prosemirror-model";
import { Plugin, Transaction } from "prosemirror-state";
import { EditorView } from "prosemirror-view";
import { defaultFontMarksNewParagraphTag } from "./defaultFontMarksPlugin";

export function insertListPlugin(
	schema: Schema<
		| "paragraph"
		| "bulletList"
		| "orderedList"
		| "listItem",
		never
	>
): Plugin<{}> {
	return new Plugin<{}>({
		props: {
			handleTextInput(
				view: EditorView,
				inputIndex: number,
				_to: number,
				text: string
			): boolean {
				if (text !== " ") {
					return false;
				}

				if (view.composing) {
					return false;
				}

				const {
					state
				} = view;

				const {
					selection
				} = state;

				if (!selection.empty) {
					return false;
				}

				const inputPosition: ResolvedPos = state.doc
					.resolve(inputIndex);

				const {
					paragraph,
					listItem
				} = schema.nodes;

				const parent: Node = inputPosition.parent;
				const grandParent: Node = inputPosition.node(-1);

				if (parent.type !== paragraph) {
					return false;
				}

				if (grandParent.type === listItem) {
					return false;
				}

				const markerLength: number = inputPosition.parentOffset;

				if (markerLength > 2) {
					return false;
				}

				const marker: string = parent.textBetween(
					0,
					markerLength,
					undefined,
					"\ufffc"
				);

				const listType: TryResult<NodeType> = tryResolveListType(marker, schema);

				if (!listType.wasSuccessful) {
					return false;
				}

				const start: number = inputIndex - markerLength;
				const end: number = inputIndex;

				const tr: Transaction = state.tr;

				tr.delete(
					start,
					end
				);

				tr.setMeta(defaultFontMarksNewParagraphTag, true);

				const range: NodeRange | null = tr.doc
					.resolve(start)
					.blockRange();

				if (!range) {
					return false;
				}

				tr.wrap(
					range,
					[
						{
							type: listType.result
						},
						{
							type: listItem
						}
					]
				);

				view.dispatch(tr);

				return true;
			}
		}
	});
}

function tryResolveListType(marker: string, schema: Schema<"orderedList" | "bulletList", never>): TryResult<NodeType> {
	const {
		bulletList,
		orderedList
	} = schema.nodes;

	if (marker === "*" || marker === "-") {
		return {
			wasSuccessful: true,
			result: bulletList
		};
	}

	if (marker === "1.") {
		return {
			wasSuccessful: true,
			result: orderedList
		};
	}

	return { wasSuccessful: false };
}